import { validEmailDomainCheck } from '~/lib/client/fields';
import { getCookie, getParam, isFreeEmailProvider } from '~/lib/helpers';
import { HIDDEN_TYPES, replaceLinks } from '~/pages/forms/form/fields/Utils';

import CompanyName from './field/CompanyName';
import CompanySize from './field/CompanySize';
import CountryCode from './field/CountryCode';
import Select from './field/Select';

class Field extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      autofillValue: null,
      autofilled: false,
      errors: [],
      focus: false,
      isUserValue: false,
      shown: false,
      value: this.getInitialValue(),
    };
  }

  componentDidMount() {
    const { value } = this.state;

    if (value) {
      // Trigger autofill if we have an initial value
      this.props.onBlur(this);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { shown } = prevState;

    if (!shown && !this.shouldProgressiveHide()) {
      this.setState({ shown: true });
    }
  }

  getInitialValue() {
    const { field, email } = this.props;

    switch (field.type) {
      case 'email':
        return email ? email : '';
      case 'checkbox':
        return field.default_value || false;
      case 'cookie':
        return getCookie(field.slug);
      case 'param':
        return getParam(field.slug);
      case 'hidden':
        return field.default_value;
      default:
        return '';
    }
  }

  setAutofillValue(value) {
    this.setState({
      autofillValue: value,
      autofilled: true,
      value: value || '',
    });
  }

  getField() {
    return this.props.field;
  }

  getValue() {
    return this.state.value;
  }

  getAutocomplete() {
    const { field } = this.props;

    switch (field.type) {
      case 'email':
        return 'email';
      case 'given_name':
        return 'given-name';
      case 'family_name':
        return 'family-name';
      case 'company_name':
        return 'company-name';
      default:
        return 'on';
    }
  }

  isRequired() {
    if (this.isHidden()) return false;

    const { field } = this.props;

    return field.required;
  }

  isValid() {
    const { field } = this.props;

    let value = this.state.value;
    let errors = [];

    if (this.shouldProgressiveHide()) {
      return true;
    }

    if (this.isRequired() && !value) {
      errors.push('is required');
    }

    if (this.isEmail() && !/.@./.test(value)) {
      errors.push('must be an email address');
    }

    if (
      this.isEmail() &&
      field.block_free_email_providers &&
      isFreeEmailProvider(value)
    ) {
      errors.push('must be a company email address');
    }

    this.setState({ errors });

    return errors.length === 0;
  }

  isEmail() {
    const { field } = this.props;

    return field.type === 'email';
  }

  isValidEmail() {
    const { value } = this.state;
    const { block_free_email_providers } = this.props.field;

    if (!block_free_email_providers) {
      return Promise.resolve(true);
    }

    return validEmailDomainCheck(value).then((result) => {
      if (result.is_free_provider === true) {
        this.setState({
          errors: [...this.state.errors, 'must be a company email address'],
        });
        return Promise.resolve(false);
      }

      return Promise.resolve(true);
    });
  }

  isHidden() {
    const { field } = this.props;

    return HIDDEN_TYPES.includes(field.type);
  }

  shouldAutofill() {
    const { field } = this.props;
    const { isUserValue, value } = this.state;

    // Email fields can't be autofilled
    if (this.isEmail()) return false;

    // Is this field set to be autofilled
    if (!field.autofill) return false;

    // Don't step on user input if present
    if (value && isUserValue) return false;

    return true;
  }

  shouldProgressive() {
    const { field } = this.props;

    return field.progressive;
  }

  shouldProgressiveHide() {
    const { autofilled, autofillValue, isUserValue, shown } = this.state;

    // ignore not progressive fields
    if (!this.shouldProgressive()) return false;
    // ignore if autofill didn't find anything
    if (autofilled && !autofillValue) return false;
    // ignore if the user touched it
    if (isUserValue) return false;
    // ignore if it's been shown to the user
    if (shown) return false;

    return true;
  }

  getStyle() {
    let styles = {};

    if (this.shouldProgressiveHide()) {
      styles.display = 'none';
    }

    return styles;
  }

  getID() {
    return 'field-' + this.props.field.id;
  }

  getClassName() {
    const { field } = this.props;
    const { focus, errors } = this.state;

    return classnames({
      'cb-field': true,
      'cb-focus': focus,
      'cb-error': errors.length > 0,
      [`cb-${field.type}`]: true,
    });
  }

  onFocus() {
    this.setState({ focus: true });
  }

  onBlur() {
    this.setState({ focus: false, isUserValue: true }, () => {
      this.props.onBlur(this);
    });
  }

  onChange(event) {
    this.setState({ value: event.target.value });
  }

  onChangeCheckbox(event) {
    this.setState({ value: event.target.checked });
  }

  getTextInputType() {
    const { field } = this.props;

    switch (field.type) {
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      default:
        return 'text';
    }
  }

  renderCompanySize() {
    const { field } = this.props;

    return (
      <CompanySize
        id={this.getID()}
        name={field.slug}
        required={this.isRequired()}
        onBlur={this.onBlur.bind(this)}
        onFocus={this.onFocus.bind(this)}
        onChange={this.onChange.bind(this)}
        value={this.state.value}
      />
    );
  }

  renderCompanyName() {
    const { field } = this.props;

    return (
      <CompanyName
        type={this.getTextInputType()}
        id={this.getID()}
        name={field.slug}
        placeholder={field.placeholder}
        required={this.isRequired()}
        autoComplete={this.getAutocomplete()}
        onBlur={this.onBlur.bind(this)}
        onFocus={this.onFocus.bind(this)}
        onChange={this.onChange.bind(this)}
        value={this.state.value}
        onSelect={(value) => this.setState({ value })}
      />
    );
  }

  renderCountryCode() {
    const { field } = this.props;

    return (
      <CountryCode
        id={this.getID()}
        name={field.slug}
        required={this.isRequired()}
        onBlur={this.onBlur.bind(this)}
        onFocus={this.onFocus.bind(this)}
        onChange={this.onChange.bind(this)}
        value={this.state.value}
      />
    );
  }

  renderTextInput() {
    const { field } = this.props;

    return (
      <input
        type={this.getTextInputType()}
        id={this.getID()}
        name={field.slug}
        placeholder={field.placeholder}
        required={this.isRequired()}
        autoComplete={this.getAutocomplete()}
        onBlur={this.onBlur.bind(this)}
        onFocus={this.onFocus.bind(this)}
        onChange={this.onChange.bind(this)}
        value={this.state.value}
      />
    );
  }

  renderTextareaInput() {
    const { field } = this.props;

    return (
      <textarea
        id={this.getID()}
        name={field.slug}
        placeholder={field.placeholder}
        required={this.isRequired()}
        onBlur={this.onBlur.bind(this)}
        onFocus={this.onFocus.bind(this)}
        onChange={this.onChange.bind(this)}
        value={this.state.value}
      />
    );
  }

  renderCheckboxInput() {
    const { field } = this.props;

    const children = replaceLinks(field.description);

    return (
      <div className="cb-checkbox-container">
        <input
          id={this.getID()}
          type="checkbox"
          name={field.slug}
          required={this.isRequired()}
          onBlur={this.onBlur.bind(this)}
          onFocus={this.onFocus.bind(this)}
          onChange={this.onChangeCheckbox.bind(this)}
          checked={this.state.value}
          value="1"
        />
        {field.description && (
          <p onClick={() => this.setState({ value: !this.state.value })}>
            {children}
          </p>
        )}
      </div>
    );
  }

  renderSelectInput() {
    const { field } = this.props;
    const { select_options } = field;

    return (
      <Select
        id={this.getID()}
        name={field.slug}
        required={this.isRequired()}
        onBlur={this.onBlur.bind(this)}
        onFocus={this.onFocus.bind(this)}
        onChange={this.onChange.bind(this)}
        value={this.state.value}
        options={select_options}
      />
    );
  }

  renderHiddenInput() {
    const { field } = this.props;

    return (
      <input type="hidden" name={field.slug} value={field.default_value} />
    );
  }

  renderInput() {
    const { field } = this.props;

    switch (field.type) {
      case 'textarea':
        return this.renderTextareaInput();
      case 'checkbox':
        return this.renderCheckboxInput();
      case 'company_size':
        return this.renderCompanySize();
      case 'company_name':
        return this.renderCompanyName();
      case 'country_code':
        return this.renderCountryCode();
      case 'select':
        return this.renderSelectInput();
      case 'hidden':
        return this.renderHiddenInput();
      case 'cookie':
      case 'param':
        return;
      default:
        return this.renderTextInput();
    }
  }

  renderErrors() {
    const { errors } = this.state;

    if (errors.length === 0) return;

    return <div className="cb-errors">{errors}</div>;
  }

  renderLabel() {
    const { field } = this.props;

    if (this.isHidden()) return;

    return <label htmlFor={this.getID()}>{field.label}</label>;
  }

  render() {
    return (
      <div className={this.getClassName()} style={this.getStyle()}>
        {this.renderErrors()}

        {this.renderLabel()}

        {this.renderInput()}
      </div>
    );
  }
}

Field.propTypes = {
  onBlur: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired,
  email: PropTypes.string,
};

export default Field;
